import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const downloadTemplate = () => downloadFile("/wines/import/template", "cellar-import-template.xlsx");

export const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/wines/import", fd);
      setResult(data);
      toast.success(`${data.wines} wines imported`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => {
    if (!v) {
      setFile(null);
      setResult(null);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#161214] border-[#2E252A]" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Import from Excel</DialogTitle></DialogHeader>
        <p className="text-sm text-[#A89F91]">One row per wine. Columns: Wine, Producer, Vintage, Colour, Region, Purchase date, Supplier, Purchase price, Notes, Bottles, Box number, Location, Drink from, Drink until. Locations that don't exist are created. Each bottle gets its own bar code.</p>
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button"><Download className="w-4 h-4 mr-2" />Download template</Button>
        <label className="block rounded-lg border border-dashed border-[#4A3B43] p-6 text-center cursor-pointer hover:border-[#D4AF37] transition-colors">
          <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
          <Upload className="w-6 h-6 mx-auto text-[#6E665D] mb-2" />
          <span className="text-sm text-[#F5F0EB]" data-testid="import-file-name">{file ? file.name : "Choose an .xlsx or .csv file"}</span>
        </label>
        <Button disabled={!file || busy} onClick={upload} className="bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="import-submit-button">{busy ? "Importing…" : "Import"}</Button>
        {result && (
          <div className="text-sm rounded-lg bg-[#0D0A0B] p-4" data-testid="import-result">
            <p className="text-[#F5F0EB]">{result.wines} wines · {result.bottles} bottles · {result.new_locations} new locations</p>
            {result.errors.map((e) => <p key={e.row} className="text-[#E5A93C] text-xs mt-1">Row {e.row}: {e.error}</p>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
